/**
 * @file The completed consult upgrades itself when the final transcription pass lands.
 * @module hooks/useFinalTranscript
 *
 */

import { useCallback, useEffect, useRef } from "react";

import { fetchSingleCompletedAppointmentById } from "../../api/healthcare/Appointment/AppointmentApi";
import { CompletedAppointment, TranscriptStatus } from "../../types/appointments/AppointmentTypes";
import { useAnalysisPolling } from "../useAnalysisPolling";

/** 15s — the final pass takes minutes, and the practitioner is reading, not waiting. */
export const FINAL_TRANSCRIPT_POLL_INTERVAL_MS = 15_000;

/**
 * 20 minutes, after which a consult left open in a tab stops asking. A pass that has not landed
 * by then has failed, and S29 leaves the row on `live` — polling it overnight changes nothing.
 */
export const FINAL_TRANSCRIPT_POLL_MAX_MS = 20 * 60_000;

interface UseFinalTranscriptOptions {
	appointmentId: number;
	/** The status of the record currently on screen. `undefined` on a pre-S2 row ⇒ never polls. */
	status?: TranscriptStatus;
	/** The completed view only — a consult still in the room has no final pass to wait for. */
	enabled: boolean;
	/** The re-fetched record, once the final pass has landed. The caller re-renders from it. */
	onFinal: (consultation: CompletedAppointment) => void;
}

/**
 * Polls while — and only while — the record on screen says `live`.
 *
 * `onFinal` is what stops it: the caller stores the returned record, `status` becomes `final`, and
 * `useAnalysisPolling` clears its own interval on the next render. There is no separate stop flag
 * to keep in step with the rendered badge.
 */
export const useFinalTranscript = ({ appointmentId, status, enabled, onFinal }: UseFinalTranscriptOptions): void => {
	// A tick already in flight when the drawer closes must not push state into an unmounted page.
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const onPoll = useCallback(async () => {
		try {
			const { data } = await fetchSingleCompletedAppointmentById(appointmentId);
			if (!mountedRef.current || data?.transcript_status !== "final") return;

			onFinal(data);
		} catch {
			// A background refresh: the practitioner keeps the transcript they already have and the
			// next tick retries, so there is nothing to show and nothing to interrupt. Event name and
			// the appointment id only — a failed fetch of a consultation record must never put an
			// exception message on a console alongside clinical content.
			console.warn("consult.final_transcript.poll_failed", { appointment_id: appointmentId });
		}
	}, [appointmentId, onFinal]);

	useAnalysisPolling({
		active: enabled && status === "live" && appointmentId > 0,
		onPoll,
		intervalMs: FINAL_TRANSCRIPT_POLL_INTERVAL_MS,
		maxDurationMs: FINAL_TRANSCRIPT_POLL_MAX_MS,
	});
};

export default useFinalTranscript;
